import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type QueryConstraint,
} from "firebase/firestore";
import { db } from "../lib/firebase";

// Shared CRUD + realtime-listener shape reused by every couple-scoped module
// (calendar, shopping list, savings goals, todos, important dates, notes) —
// they're all "a live-synced list of documents under couples/{coupleId}/*"
// with the same add/patch/remove operations, just different fields.
export function useCoupleCollection<T extends { id: string }>(
  coupleId: string | null,
  subcollection: string,
  ...constraints: QueryConstraint[]
) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!coupleId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, "couples", coupleId, subcollection), ...constraints);
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T));
      setLoading(false);
    });
    return unsub;
    // constraints are built fresh by the caller on every render; only the
    // collection identity actually determines what we're listening to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupleId, subcollection]);

  async function add(data: DocumentData, timestampField: "createdAt" | "updatedAt" = "createdAt") {
    const ref = await addDoc(collection(db, "couples", coupleId!, subcollection), {
      ...data,
      [timestampField]: serverTimestamp(),
    });
    return ref.id;
  }
  async function patch(id: string, data: DocumentData, timestampField?: "updatedAt") {
    await updateDoc(doc(db, "couples", coupleId!, subcollection, id), {
      ...data,
      ...(timestampField ? { [timestampField]: serverTimestamp() } : {}),
    });
  }
  async function remove(id: string) {
    await deleteDoc(doc(db, "couples", coupleId!, subcollection, id));
  }

  return { items, loading, add, patch, remove };
}
